import { useEffect, useState } from "react";

import { GetScaleOrdersResponseProto } from "@noom/noom-contracts/noom_contracts/backend/scales/scale_order_service";
import { GetUserModelResponseProto } from "@noom/noom-contracts/noom_contracts/usermodel/service/v3/user_model_service";
import { Api } from "@noom/noomscape";

import { useAppContext } from "@/contexts";
import { getAutologinData } from "@/utils/cookies";

import { useMemberPartner } from "./useMemberPartner";

export class MissingAutologinError extends Error {
  constructor() {
    super("No autologin data found");
    this.name = "MissingAutologinError";
    // Needed for correct instanceof checks in transpiled environments.
    Object.setPrototypeOf(this, MissingAutologinError.prototype);
  }
}

export type ScaleOptInEligibilityResult = {
  canShowOptIn: boolean;
  isLoading: boolean;
  error: unknown;
};

function hasMedEntitlement(
  userModelResponse: GetUserModelResponseProto,
): boolean {
  const entitlements = userModelResponse.userModel?.baselined?.entitlements;
  return !!(entitlements?.telehealth || entitlements?.noomMedPoweredByAccolade);
}

export const useScaleOptInEligibility = (): ScaleOptInEligibilityResult => {
  const { setEligibilityInfo } = useAppContext();
  const [isLoading, setIsLoading] = useState(true);
  const [canShowOptIn, setCanShowOptIn] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Also saves the partner in app state so survey events carry its ID and name.
  const {
    partner,
    isLoading: isPartnerLoading,
    error: partnerError,
  } = useMemberPartner();

  useEffect(() => {
    const autologinData = getAutologinData();
    if (!autologinData) {
      setIsLoading(false);
      setError(new MissingAutologinError());
      return () => undefined;
    }

    // Wait for the partner first. A member with no partner can't get a scale,
    // so don't fetch their orders; the caller shows the error screen.
    if (isPartnerLoading) {
      return () => undefined;
    }
    if (partnerError || !partner) {
      setError(partnerError ?? new Error("No partner found for user"));
      setIsLoading(false);
      return () => undefined;
    }

    const { accessCode, accessToken } = autologinData;

    let ignore = false;

    async function fetchEligibility() {
      try {
        const ordersResponse = (await Api.call(
          "scales.getOrders",
          Api.api.scales.getOrders,
          { accessCode, accessToken },
        )) as GetScaleOrdersResponseProto;

        if (ignore) {
          return;
        }

        const hasOrder = (ordersResponse.orders ?? []).length > 0;
        const { scaleEligibility } = ordersResponse;

        let eligible = false;
        if (!hasOrder) {
          if (
            scaleEligibility === "FREE_SCALE" ||
            scaleEligibility === "NOOMCOIN" ||
            scaleEligibility === "INCLUDED_SCALE"
          ) {
            eligible = true;
          } else if (scaleEligibility === "MED_ONLY") {
            const userModelResponse = (await Api.call(
              "userModel.getUserModel",
              Api.api.userModel.getUserModel,
              {
                accessCode,
                accessToken,
                fieldMask: "baselined.entitlements",
              },
            )) as GetUserModelResponseProto;

            if (ignore) {
              return;
            }

            eligible = hasMedEntitlement(userModelResponse);
          }
        }

        // The scale questions route on eligibilityInfo, which this page never
        // loads. Set it before isLoading flips so the survey sees it on advance.
        setEligibilityInfo({ scaleEligible: scaleEligibility });
        setCanShowOptIn(eligible);
      } catch (e) {
        if (ignore) {
          return;
        }
        setError(e);
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    }

    fetchEligibility();

    return () => {
      ignore = true;
    };
  }, [setEligibilityInfo, isPartnerLoading, partner, partnerError]);

  return { canShowOptIn, isLoading, error };
};
